// Imports
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit } from '@angular/core';
import { IdynamicFormItem } from '@ispm/shared/dynamic-form/interfaces/IdynamicFormItem';
import { IWidgetItem } from '@ispm-client/modules/widgets/interfaces/widget-item.interface';
import { Widget } from '@ispm-client/modules/widgets/decorator/widget.decorator';
import { DynamicFormItem } from '@ispm/shared/dynamic-form/classes/dynamic-form-item.class';
import { DecisionTreeInputs, IDecisionTreeCapturePayload, UpdateMDTTypeFormChangeModel } from './interfaces/mdt-decision-tree.interface';
import { FormGroup } from '@angular/forms';
import { ITEMS_TYPES } from '@ispm/shared/dynamic-form/constants/ItemsTypes';
import { MDTTypePayload, MDTTypePayloadTypeConfig } from './interfaces/mdt-type-map.interface';
import { get, isEmpty } from 'lodash';
import { IoptionElemet } from '@ispm/shared/interfaces/IoptionElemet';
import { IReflect } from '@ispm/shared/dynamic-form/interfaces/reflect.interface';
import { MDTDecisionTreeService } from './services/mdt-decision-tree.service';

@Component({
  selector: 'ispm-decision-tree-capture',
  templateUrl: './decision-tree-capture.component.html',
  styleUrls: ['./decision-tree-capture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@Widget('DynamicMDTGrouping')
export class DecisionTreeCaptureComponent implements OnInit, IWidgetItem<IDecisionTreeCapturePayload> {
  widgetUpdateValueCallback: (value) => void;
  public widgetConfigPayload: IDecisionTreeCapturePayload;
  private _widgetConfig: IdynamicFormItem = <IdynamicFormItem>{};
  public data: Array<any>[];
  public inputConfig: DynamicFormItem[];
  public widgetFormInstance: FormGroup;
  public submitted: boolean = false;
  private mdtTypeConfigurations: MDTTypePayloadTypeConfig[] = [];
  public isValid: boolean = true;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private mdtDecisionService: MDTDecisionTreeService
  ) {}

  /**
   * Angular life cycle hook
   */
  public ngOnInit() {
    this.fetchMDTTypeConfiguration();
  }

  /**
   * Getter for widget config
   */
   get widgetConfig(): IdynamicFormItem {
    return this._widgetConfig;
  }

  /**
   * Setter for widget config
   * @param config holds widget config data
   */
  set widgetConfig(config: IdynamicFormItem) {
    this._widgetConfig = config;
    this.widgetConfigPayload = config.widgetPayload;
    if (this.widgetConfigPayload) {
      this.inputConfig = this.createInputConfig();
    }
  }

  /**
   * Fetches mdt type configuation from API
   */
  private fetchMDTTypeConfiguration() {
    this.mdtDecisionService.getAllMDTConfiguration(this.widgetConfigPayload.resourceLocation).subscribe((config) => {
      this.inputConfig = this.createMDTType(this.getMDTGroupName(get(config[0],'[0].response.entry.0.resource.payload',{}), 
                                get(config[1],'[0].response.entry.0.resource.payload',{})));
      this.changeDetectorRef.markForCheck();
      this.changeDetectorRef.detectChanges();
    });
  }

  /**
   * Creates and return input config for dynamic form
   * @returns DynamicFormItem[]
   */
  private createMDTType(mdtTypeOptions:IoptionElemet[]): DynamicFormItem[] {
    return this.inputConfig.map((input: DynamicFormItem) => {
      if(input.name === 'MDTType') {
        input.setOptions(mdtTypeOptions);
        input.addReflects()
        input.reflects = this.prepareReflects(input);
      }
      return input;
    });
  }

  /**
   * Creates and return input config for dynamic form
   * @returns DynamicFormItem[]
   */
  private createInputConfig(): DynamicFormItem[] {
    return this.widgetConfigPayload.inputs.map((input: DecisionTreeInputs) => this.buildFormItems(input));
  }

  /**
   * Build individual form items
   * @param input holds form input
   * @returns DynamicFormItem
   */
  private buildFormItems(input: DecisionTreeInputs): DynamicFormItem {
    return DynamicFormItem.builder(
      input.label || '',
      input.name,
      ITEMS_TYPES.DROPDOWN
    ).setCssClass(input.cssClass).setPlaceholder(input.placeholder).setOptions(input.options.resource)
        .setAttributes(input.attributes).setRequired(input.required)
  }

  /**
   * Creates list of MDTGroup Name
   * @param defaultConfig holds default MDT group payload
   * @param organizationConfig holds organization specific MDT group payload
   */
  private getMDTGroupName(defaultConfig: MDTTypePayload, organizationConfig: MDTTypePayload): IoptionElemet[] {
    let mergedDefaultConfig: MDTTypePayloadTypeConfig[] = [];
    let mergedOrganizationConfig: MDTTypePayloadTypeConfig[] = [];
    if (defaultConfig && !isEmpty(defaultConfig.typeConfig)) {
      mergedDefaultConfig = this.mdtDecisionService.processDuplicateMDTType(defaultConfig.typeConfig);
      if(organizationConfig && !isEmpty(organizationConfig.typeConfig)) {

        if(organizationConfig.overrides && organizationConfig.overrides.removeMDTType) {
          mergedDefaultConfig = mergedDefaultConfig.filter((config) => !organizationConfig.overrides.removeMDTType.includes(config.mdtType));
        }

        mergedOrganizationConfig = this.mdtDecisionService.processDuplicateMDTType(organizationConfig.typeConfig);
        // Priority is given to organization specific resource with same type
        this.mdtTypeConfigurations = [...new Map(mergedDefaultConfig.concat(mergedOrganizationConfig)
          .map((config) => [config['mdtType'], config])).values()];
      }

      else this.mdtTypeConfigurations = mergedDefaultConfig;
      return this.mdtTypeConfigurations.map((config) => <IoptionElemet>{
        code: config.mdtType,
        display: config.mdtTypeDispaly
      });
    }
  }

  /**
   * Prepares all reflects
   * @param config holds mdt type config
   */
  private prepareReflects(config: DynamicFormItem): IReflect[] {
    const diseaseReflects: IReflect[] = this.mdtTypeConfigurations.map((mdtTypeConfig: MDTTypePayloadTypeConfig) => {
      return this.prepareReflectItems('disease', mdtTypeConfig.mdtTypeDispaly, this.getCancerTypeResource(mdtTypeConfig.mdtType));
    });
    const clinicalPhaseReflects: IReflect[] = this.mdtTypeConfigurations.map((mdtTypeConfig: MDTTypePayloadTypeConfig) => 
                 this.prepareReflectItems('clinicalPhase', mdtTypeConfig.mdtTypeDispaly, this.getClinicalPhaseTypeResource(mdtTypeConfig.mdtType)));
    return diseaseReflects.concat(clinicalPhaseReflects);
  }

  /**
   * Prepares individual reflects
   * @param affectedField holds affected field on regex application
   * @param applyRegex holds the match regex
   * @param resource contains resources to be set
   * @returns IReflect
   */
  private prepareReflectItems(affectedField: string, applyRegex: string, resource: string): IReflect {
    return {
      affectedField: affectedField,
      applyRegex: `^${applyRegex}$`,
      valueToSet: null,
      checkOnInit: true,
      options: {
        values: null,
        resource: resource
      }
    }
  }

  /**
   * Prepares cancer type resources
   * @param mdtType holds mdttype value
   */
   private getCancerTypeResource(mdtType: string): string {
    const cancerType: string = this.mdtTypeConfigurations.find((config) => config.mdtType === mdtType).cancerType;
    return `/ValuesetFlattenAndFilter/ICD10NeoplasmsCodes?searchValue={${cancerType}},$(searchTerm)&_sort:asc=display&_includeParents=true&startsWith=true|transform=display:display,code:code`
  }

  /**
   * Prepares clinical phase  resources
   * @param mdtType holds mdttype value
   */
  private getClinicalPhaseTypeResource(mdtType: string): string {
    const clinicalPhase: string = this.mdtTypeConfigurations.find((config) => config.mdtType === mdtType).clinicalPhase;
    return `/Valueset/clinical-phase-codes?searchValue=${clinicalPhase}&locale=en-US&_sort:desc=locale`;
  }

  /**
   * Calls on any form change
   * @param form holds form instance
   * @param updatedField holds the list of updated fields
   */
  public updateFormOnChange(form: UpdateMDTTypeFormChangeModel, _updatedField: string[]) {
    if(this.widgetUpdateValueCallback) this.widgetUpdateValueCallback(form);
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Calls on validating the form
   * @param validate holds boolean value
   */
  public updateValidate(validate: boolean) {
    this.isValid = validate;
  }

  /**
   * Setter for widget data
   */
  set widgetData(_widgetData: any) {}

  widgetValidator(): boolean {
    return this.isValid;
  }

  /**
   * Getter for widget submit status
   */
  public get widgetFormInstanceSubmitted(){
    return this.submitted;
  }

  /**
   * Sets form submitted instance
   * @param widgetFormInstanceSubmitted holds boolean for submitted status
   */
  public set widgetFormInstanceSubmitted(_widgetFormInstanceSubmitted: boolean) {
    this.submitted = true;
    this.changeDetectorRef.markForCheck();
    this.changeDetectorRef.detectChanges();
  }
}